import { zodResolver } from '@hookform/resolvers/zod';
import { Add } from '@mui/icons-material';
import { Grid, Typography, Button, CircularProgress } from '@mui/material';
import { CreateRecurringInterval, UpdateLenderAvailability, Weekday } from '@thro-types/leihbar';

import { omit } from 'lodash';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLenderAvailabilityMutation, useLenderAvailabilityQuery } from 'api/hooks/leihbar';
import { SaveButton } from 'app/components';
import DetailPage from 'app/components/DetailPage';
import Subheading from 'app/components/DetailPage/components/Subheading';
import { RecurringIntervalList } from 'app/components/RecurringIntervalList';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { initialOpeningHoursValues, lenderZodSchema } from '../validation';

interface LenderTimesProps {
  lenderGroupId: number;
}

export const LenderTimes = ({ lenderGroupId }: LenderTimesProps) => {
  const { t } = useTranslation();

  const { update } = useLenderAvailabilityMutation();

  const dispatch = useDispatch();

  const { data: lenderAvailability, isLoading } = useLenderAvailabilityQuery(lenderGroupId);

  const { handleSubmit, setValue, control, watch } = useForm<UpdateLenderAvailability>({
    resolver: zodResolver(lenderZodSchema(t)),
    values: initialOpeningHoursValues(lenderAvailability)
  });

  const handleSubmitting = async (values: { availabilityTimes: CreateRecurringInterval[] }) => {
    try {
      const filteredTimes = values.availabilityTimes.map(item => omit(item, 'availabilityTimesId'));
      await update.mutateAsync({
        id: lenderGroupId,
        data: { availabilityTimes: filteredTimes }
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.leihbarOpeningHoursSettings.lendertimes.alerts.successfulUpdate())
        )
      );
    } catch (err) {
      dispatch(
        AlertActions.error(
          t(translations.pages.leihbarOpeningHoursSettings.lendertimes.alerts.failedUpdate())
        )
      );
    }
  };

  return (
    <DetailPage>
      <form onSubmit={handleSubmit(handleSubmitting)}>
        <Grid container>
          <Subheading
            content={t(
              translations.pages.leihbarOpeningHoursSettings.lendertimes.availabilityTimes.title()
            )}
          />
          <Grid container gap={2} marginRight={5}>
            {!!watch('availabilityTimes').length && (
              <Grid container item xs={8} gap={1} className="appear">
                <Typography>
                  {t(
                    translations.pages.leihbarOpeningHoursSettings.lendertimes.availabilityTimes.text1()
                  )}
                </Typography>
                <Typography>
                  {t(
                    translations.pages.leihbarOpeningHoursSettings.lendertimes.availabilityTimes.text2()
                  )}
                </Typography>
                <Typography>
                  {t(
                    translations.pages.leihbarOpeningHoursSettings.lendertimes.availabilityTimes.text3()
                  )}
                </Typography>
              </Grid>
            )}
            <Grid container justifyContent={'center'} marginTop={2} gap={2} alignItems={'center'}>
              {isLoading ? (
                <CircularProgress />
              ) : (
                <Controller
                  control={control}
                  name="availabilityTimes"
                  rules={{ required: true }}
                  render={({ field }) =>
                    lenderAvailability && !field.value.length ? (
                      <>
                        <Grid item>
                          {t(
                            translations.pages.leihbarOpeningHoursSettings.lendertimes.noLenderTimeAvailable()
                          )}
                        </Grid>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const newIntervals = [...field.value];
                            newIntervals.push({
                              weekdays: [
                                Weekday.MON,
                                Weekday.TUE,
                                Weekday.WED,
                                Weekday.THU,
                                Weekday.FRI
                              ],
                              start: 480,
                              end: 960
                            });
                            setValue('availabilityTimes', newIntervals, { shouldValidate: true });
                          }}
                        >
                          <Add />
                        </Button>
                      </>
                    ) : (
                      <Grid item xs={11} justifyContent={'space-between'} className="appear">
                        <Grid container item justifyContent="end">
                          <Button
                            variant="outlined"
                            onClick={() => {
                              const newIntervals = [...field.value];
                              newIntervals.push({
                                weekdays: [
                                  Weekday.MON,
                                  Weekday.TUE,
                                  Weekday.WED,
                                  Weekday.THU,
                                  Weekday.FRI
                                ],
                                start: 480,
                                end: 960
                              });
                              setValue('availabilityTimes', newIntervals, { shouldValidate: true });
                            }}
                            style={{ marginBottom: 5, minHeight: 50 }}
                          >
                            <Add />
                          </Button>
                        </Grid>

                        <RecurringIntervalList
                          fieldName="availabilityTimes"
                          values={field.value}
                          setFieldValue={setValue}
                          isLoading={isLoading}
                        />
                        <Grid item xs alignSelf={'flex-end'} textAlign={'right'} marginTop={4}>
                          <SaveButton isSubmitting={update.isPending} />
                        </Grid>
                      </Grid>
                    )
                  }
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </form>
    </DetailPage>
  );
};
